/*
 * Team derivations.
 *
 * The list endpoint populates both sides:
 *
 *   { _id, name, description,
 *     manager: { _id, name, email } | null,
 *     members: [{ _id, name, email }] }
 *
 * so a team's own `members` array is what the cards count, while a user's
 * `team` reference is what the Users page filters on. They are two records of
 * one fact and they can disagree — see backend/scripts/verify-team-membership.js
 * for the write paths that used to let them.
 */

/** Deterministic accent per team, so a team keeps its colour across renders. */
const TEAM_TONES = [
    "bg-brand-600",
    "bg-violet-600",
    "bg-emerald-600",
    "bg-orange-500",
    "bg-cyan-600",
    "bg-pink-600",
];

import { activeOnly } from "@/lib/users.js";

export const teamTone = (index) => TEAM_TONES[index % TEAM_TONES.length];

export const idOf = (value) => (typeof value === "string" ? value : (value?._id ?? null));

export const memberIds = (team) => (team?.members ?? []).map(idOf).filter(Boolean);

/**
 * Everyone with no team.
 *
 * Derived from the USERS list rather than by subtracting team members, because
 * the two can disagree — and when they do, the person who is missing from every
 * team's list is the one an admin needs to see here.
 */
export function unassignedUsers(users) {
    // Active only: a deactivated account is not "waiting to be assigned", and
    // counting it as such makes the number a manager acts on wrong.
    return activeOnly(users)
        .filter((u) => !u.team)
        .sort((a, b) => a.name.localeCompare(b.name));
}

export function teamStats(teams, users) {
    const people = activeOnly(users);
    const assigned = people.filter((u) => u.team).length;
    return {
        teams: teams.length,
        assigned,
        unassigned: people.length - assigned,
        withoutManager: teams.filter((t) => !t.manager).length,
    };
}

/**
 * Which OTHER team does this person currently belong to?
 *
 * Shown on each addable row so the consequence is visible before the click:
 * adding them here takes them out of that team.
 */
export function currentTeamName(user, teams) {
    const id = idOf(user.team);
    if (!id) return null;
    return teams.find((t) => t._id === id)?.name ?? null;
}
